export default class NineSlicedSprite extends Phaser.GameObjects.Sprite {
	private _baseTexture: Phaser.Textures.TextureSource;
	private _renderTexture: Phaser.GameObjects.RenderTexture;
	private _baseKey: string;
	private _renderTextureKey: string;
	private _top: number;
	private _right: number;
	private _bottom: number;
	private _left: number;
	private _height: number;
	private _width: number;

	constructor(
		scene: Phaser.Scene,
		x: number,
		y: number,
		key: string,
		width: number,
		height: number,
		top: number,
		right: number,
		bottom: number,
		left: number,
		onRender?: () => any,
		useRenderHack = true
	) {
		let renderTextureKey = key + "-rt";
		let index = 0;
		while (scene.textures.exists(renderTextureKey)) {
			index++;
			renderTextureKey = key + "-rt-" + index;
		}

		super(scene, x, y, renderTextureKey);

		this._baseKey = key;
		this._renderTextureKey = renderTextureKey;

		this._top = top;
		this._right = right;
		this._bottom = bottom;
		this._left = left;

		this.resize(width, height);
		this.setTexture(renderTextureKey);
		this.setOrigin(0.5, 0.5);

		this.once(Phaser.GameObjects.Events.DESTROY, (): void => {
			scene.textures.remove(this._renderTextureKey);
		});

		//Kludge: Re-rendering here fixes issues related to being in a scene with Phaser3D
		//Without this the webgl texture can end up empty
		if (useRenderHack) {
			this.scene.events.once(Phaser.Scenes.Events.RENDER, (): void => {
				if (this.scene && this.scene.sys) {
					this.resize(width, height);
					this.setTexture(renderTextureKey);
					this.setOrigin(0.5, 0.5);
					if (onRender) onRender();
				}
			});
		}
	}

	get slicedHeight(): number {
		return this._height;
	}

	set slicedHeight(value: number) {
		this.resize(this._width, value);
	}

	get slicedWidth(): number {
		return this._width;
	}

	set slicedWidth(value: number) {
		this.resize(value, this._height);
	}

	resize(width: number, height: number): void {
		if (this.scene == null) return;
		if (isNaN(width)) width = 1;
		if (isNaN(height)) height = 1;
		width = Math.max(width, 1);
		height = Math.max(height, 1);

		if (this._renderTexture == null) {
			this._renderTexture = new Phaser.GameObjects.RenderTexture(this.scene, 0, 0, width, height);
			this._renderTexture.setOrigin(0, 0);
		} else {
			this._renderTexture.clear();
			this._renderTexture.resize(width, height);
		}
		this._width = width;
		this._height = height;

		if (this._width < this._right + this._left) {
			this._width = this._right + this._left; //kludge: if our slices are greater than our width just force our width to match to avoid rendering issues
		}
		if (this._height < this._top + this._bottom) {
			this._height = this._top + this._bottom; //kludge: if our slices are greater than our height just force our height to match to avoid rendering issues
		}

		this._baseTexture = this.scene.textures.get(this._baseKey).source[0]; //todo: could their be multiple?

		//top left
		this.renderPiece(0, 0, new Phaser.Geom.Rectangle(0, 0, this._left, this._top));

		//top right
		this.renderPiece(
			width - this._right,
			0,
			new Phaser.Geom.Rectangle(this._baseTexture.width - this._right, 0, this._right, this._top)
		);

		//top middle
		this.renderStretchPiece(
			this._left,
			0,
			new Phaser.Geom.Rectangle(this._left, 0, this._baseTexture.width - this._left - this._right, this._top),
			width - this._left - this._right,
			this._top
		);

		//bottom left
		this.renderPiece(
			0,
			height - this._bottom,
			new Phaser.Geom.Rectangle(0, this._baseTexture.height - this._bottom, this._left, this._bottom)
		);

		//bottom right
		this.renderPiece(
			width - this._right,
			height - this._bottom,
			new Phaser.Geom.Rectangle(
				this._baseTexture.width - this._right,
				this._baseTexture.height - this._bottom,
				this._right,
				this._bottom
			)
		);

		//bottom middle
		this.renderStretchPiece(
			this._left,
			height - this._bottom,
			new Phaser.Geom.Rectangle(
				this._left,
				this._baseTexture.height - this._bottom,
				this._baseTexture.width - this._left - this._right,
				this._bottom
			),
			width - this._left - this._right,
			this._bottom
		);

		//left middle
		this.renderStretchPiece(
			0,
			this._top,
			new Phaser.Geom.Rectangle(0, this._top, this._left, this._baseTexture.height - this._top - this._bottom),
			this._left,
			height - this._top - this._bottom
		);

		//right middle
		this.renderStretchPiece(
			width - this._right,
			this._top,
			new Phaser.Geom.Rectangle(
				this._baseTexture.width - this._right,
				this._top,
				this._right,
				this._baseTexture.height - this._top - this._bottom
			),
			this._right,
			height - this._top - this._bottom
		);

		//middle
		this.renderStretchPiece(
			this._left,
			this._top,
			new Phaser.Geom.Rectangle(
				this._left,
				this._top,
				this._baseTexture.width - this._left - this._right,
				this._baseTexture.height - this._top - this._bottom
			),
			width - this._left - this._right,
			height - this._top - this._bottom
		);

		this._renderTexture.saveTexture(this._renderTextureKey);
	}

	renderPiece(x: number, y: number, frame: Phaser.Geom.Rectangle): void {
		const sprite = new Phaser.GameObjects.Sprite(this.scene, 0, 0, this._baseKey);
		sprite.frame = new Phaser.Textures.Frame(sprite.texture, "", 0, frame.x, frame.y, frame.width, frame.height);
		sprite.setSizeToFrame(sprite.frame);
		sprite.setOrigin(0, 0);
		this._renderTexture.draw(sprite, x, y);
		sprite.setCrop();
		sprite.destroy();
	}

	renderStretchPiece(x: number, y: number, frame: Phaser.Geom.Rectangle, width: number, height: number): void {
		const sprite = new Phaser.GameObjects.Sprite(this.scene, 0, 0, this._baseKey);
		sprite.frame = new Phaser.Textures.Frame(sprite.texture, "", 0, frame.x, frame.y, frame.width, frame.height);
		sprite.setSizeToFrame(sprite.frame);
		sprite.setDisplaySize(width, height);
		sprite.setOrigin(0, 0);
		this._renderTexture.draw(sprite, x, y);
		sprite.destroy();
	}

	destroy(): void {
		super.destroy();
	}
}
